import { RiskBadge } from "@caint/design-system";
import type { Momento, NivelRisco } from "@caint/engine";
import { useMemo, useState } from "react";
import { engine } from "../../lib/engine.ts";
import type { ItemMerged } from "../../state/useAppState.ts";
import { type Coluna, PageHeader, Tabela } from "../ui/kit.tsx";

export interface RiscosDesktopProps {
  items: ItemMerged[];
  momento: Momento;
}

interface LinhaRisco {
  item: ItemMerged;
  valor: number;
  nivel: NivelRisco;
}

const NIVEIS: { chave: NivelRisco | "todos"; rotulo: string }[] = [
  { chave: "todos", rotulo: "Todos" },
  { chave: "RC", rotulo: "Crítico" },
  { chave: "RA", rotulo: "Alto" },
  { chave: "RM", rotulo: "Moderado" },
  { chave: "RP", rotulo: "Pequeno" },
];

export function RiscosDesktop({ items, momento }: RiscosDesktopProps) {
  const [filtro, setFiltro] = useState<NivelRisco | "todos">("todos");
  const riscos = useMemo<LinhaRisco[]>(
    () =>
      items
        .map((item) => {
          const valor = engine.riscoResidual(item, momento);
          const nivel = engine.nivelRisco(valor);
          return valor != null && nivel ? { item, valor, nivel } : null;
        })
        .filter((x): x is LinhaRisco => x !== null)
        .sort((a, b) => b.valor - a.valor),
    [items, momento],
  );
  const noPlano = riscos.filter((r) => r.valor >= 8).length;
  const visiveis = filtro === "todos" ? riscos : riscos.filter((r) => r.nivel === filtro);
  const contagem = (n: NivelRisco) => riscos.filter((r) => r.nivel === n).length;

  const colunas: Coluna<LinhaRisco>[] = [
    {
      rotulo: "Código",
      largura: "90px",
      celula: (r) => <span className="chip chip-code">{r.item.code}</span>,
    },
    { rotulo: "Item", celula: (r) => r.item.pergunta },
    { rotulo: "P×I", largura: "64px", num: true, celula: (r) => engine.riscoInerente(r.item) },
    { rotulo: "Residual", largura: "120px", celula: (r) => <RiskBadge value={r.valor} withVal /> },
    {
      rotulo: "Plano de Ação",
      largura: "130px",
      celula: (r) =>
        r.valor >= 8 ? (
          <span style={{ color: "var(--warning)", fontSize: 12.5 }}>
            ⚠️ {r.valor > 14 ? "15 dias" : "60 dias"}
          </span>
        ) : (
          <span className="spec">—</span>
        ),
    },
  ];

  return (
    <div className="dk-page">
      <PageHeader
        titulo="Riscos"
        sub={`${riscos.length} itens avaliados · ${noPlano} no plano de ação (≥ 8)`}
      />
      <div className="m-seg" style={{ marginBottom: 16, maxWidth: 520 }}>
        {NIVEIS.map((n) => (
          <button
            key={n.chave}
            type="button"
            className={filtro === n.chave ? "on" : ""}
            onClick={() => setFiltro(n.chave)}
          >
            {n.rotulo}
            {n.chave !== "todos" ? ` ${contagem(n.chave)}` : ""}
          </button>
        ))}
      </div>
      <Tabela colunas={colunas} linhas={visiveis} chave={(r) => r.item.id} vazio="Nenhum item neste nível." />
    </div>
  );
}
